import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';

const statusInfo = (ind) => {
  if (ind.status === 'invited') {
    const expired = ind.tempExpiresAt && new Date(ind.tempExpiresAt) <= new Date();
    return expired
      ? { label:'Login expired',  bg:'var(--red-bg)',   color:'var(--red)'   }
      : { label:'Awaiting worker', bg:'var(--amber-bg)', color:'var(--amber)' };
  }
  if (ind.status === 'awaiting_supervisor') return { label:'Awaiting sign-off', bg:'var(--blue-light)', color:'var(--blue)' };
  return { label:'Completed', bg:'var(--green-bg)', color:'var(--green)' };
};

export default function StaffInductionList() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const isOwner   = user?.role === 'owner';
  const canEdit   = can('canManageStaff') || isOwner;

  const [deletingId, setDeletingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [notice,      setNotice]      = useState(null); // { type, text }

  const [inductions, setInductions] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/staff-inductions', { params: { page, limit: 15 } });
      setInductions(data.inductions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {}
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const thisMonth = inductions.filter(i => {
    const d = new Date(i.createdAt); const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const handleDownloadPDF = (id) => {
    const token   = localStorage.getItem('cs_token');
    const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
    window.open(`${baseURL}/api/staff-inductions/${id}/pdf?token=${token}`, '_blank');
  };

  const handleResend = async (ind) => {
    if (!window.confirm(`Send ${ind.workerName} a new login at ${ind.inviteEmail}? Their previous login will stop working.`)) return;
    setResendingId(ind._id); setNotice(null);
    try {
      await api.post(`/staff-inductions/${ind._id}/resend`);
      setNotice({ type:'success', text:`A new login was emailed to ${ind.inviteEmail}. It's valid for 24 hours.` });
      fetchAll();
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Could not send the new login.' });
    } finally { setResendingId(null); }
  };

  const handleDelete = async (id, workerName) => {
    if (!await confirmDelete(`This induction record for "${workerName}", including both signatures, will be permanently removed.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/staff-inductions/${id}`);
      setInductions(prev => prev.filter(i => i._id !== id));
      setTotal(t => t - 1);
    } catch {}
    finally { setDeletingId(null); }
  };

  const th = { padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'11px 12px', fontSize:'13px', color:'var(--text)', verticalAlign:'middle' };

  return (
    <div style={T.page}>
      <PageHeader title="Staff Induction" actions={
        <button onClick={()=>navigate('/staff-induction/new')} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> New Induction
        </button>
      }/>

      <div style={{ maxWidth:'1000px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:'10px', marginBottom:'16px' }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
            <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'6px' }}>Total Inductions</div>
            <div style={{ fontSize:'22px', fontWeight:'500', color:'var(--blue)' }}>{total}</div>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
            <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'6px' }}>This Month</div>
            <div style={{ fontSize:'22px', fontWeight:'500', color:'var(--green)' }}>{thisMonth}</div>
          </div>
        </div>

        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--muted)' }}>
              <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
            </div>
          ) : inductions.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center' }}>
              <i className="ti ti-shield-check" style={{ fontSize:'32px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>No staff inductions yet</div>
              <button onClick={()=>navigate('/staff-induction/new')} style={T.btnPrimary}>New Induction</button>
            </div>
          ) : (
            <div className="table-scroll">
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['Worker','Position','Site','Status','Information Recorded','Inducted By','Created','Submitted By','Actions'].map(h=>(
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inductions.map(ind => { const st = statusInfo(ind); return (
                    <tr key={ind._id} onClick={()=>navigate(`/staff-induction/${ind._id}`)}
                      style={{ cursor:'pointer', transition:'background .1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={td}><span style={{ fontWeight:'500' }}>{ind.workerName}</span></td>
                      <td style={td}>{ind.position}</td>
                      <td style={td}><span style={{ color:'var(--blue)' }}>{ind.site}</span></td>
                      <td style={td}>
                        <span style={{ background:st.bg, color:st.color, fontSize:'11px', fontWeight:'500', padding:'2px 9px', borderRadius:'20px', whiteSpace:'nowrap' }}>{st.label}</span>
                        {ind.status === 'invited' && ind.tempExpiresAt && st.label !== 'Login expired' && (
                          <div style={{ fontSize:'10px', color:'var(--muted)', marginTop:'3px', whiteSpace:'nowrap' }}>
                            Expires {new Date(ind.tempExpiresAt).toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </div>
                        )}
                      </td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px' }}>{ind.startDate ? new Date(ind.startDate).toLocaleDateString('en-AU') : '—'}</span></td>
                      <td style={td}>{ind.inductedBy}</td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', color:'var(--muted)' }}>{new Date(ind.createdAt).toLocaleDateString('en-AU')}</span></td>
                      <td style={td}><span style={{ fontSize:'12px', color:'var(--muted)' }}>{ind.submittedByName || ind.submittedBy?.name || '—'}</span></td>
                      <td style={td} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', gap:'4px' }}>
                          {canEdit && ind.status === 'invited' && ind.inviteEmail && (
                            <button onClick={()=>handleResend(ind)} disabled={resendingId===ind._id} title="Send a new login"
                              style={{ ...T.btnIcon, color:'var(--purple)', borderColor:'rgba(83,74,183,0.25)', opacity:resendingId===ind._id?0.5:1 }}>
                              <i className="ti ti-send" style={{ fontSize:'13px' }} aria-label="Send a new login"/>
                            </button>
                          )}
                          {ind.status !== 'invited' && (   // nothing to download until the worker has filled it in
                            <button onClick={()=>handleDownloadPDF(ind._id)} title="Download PDF" style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                              <i className="ti ti-file-download" style={{ fontSize:'13px' }} aria-label="Download PDF"/>
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={()=>navigate(`/staff-induction/${ind._id}/edit`)}
                              title={ind.status === 'awaiting_supervisor' ? 'Review and sign off' : 'Edit'}
                              style={ind.status === 'awaiting_supervisor'
                                ? { ...T.btnIcon, color:'#fff', background:'var(--blue)', borderColor:'var(--blue)' }
                                : { ...T.btnIcon, color:'var(--amber)', borderColor:'rgba(133,79,11,0.25)' }}>
                              <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-label={ind.status === 'awaiting_supervisor' ? 'Review and sign off' : 'Edit'}/>
                            </button>
                          )}
                          {isOwner && (
                            <button onClick={()=>handleDelete(ind._id, ind.workerName)} disabled={deletingId===ind._id} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                              <i className="ti ti-trash" style={{ fontSize:'13px' }} aria-label="Delete"/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'12px', borderTop:'1px solid var(--border)' }}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ ...T.btnSecondary, opacity:page===1?0.4:1 }}>
                <i className="ti ti-chevron-left" aria-label="Previous"/>
              </button>
              <span style={{ fontSize:'13px', color:'var(--muted)', fontFamily:'JetBrains Mono,monospace' }}>{page} / {pages}</span>
              <button onClick={()=>setPage(p=>Math.min(pages,p+1))} disabled={page===pages} style={{ ...T.btnSecondary, opacity:page===pages?0.4:1 }}>
                <i className="ti ti-chevron-right" aria-label="Next"/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
